import { useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getUploadStatus } from "@services/API/Requests";
import { postContactArray } from "@services/API/Requests/ContactRequests";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { GetCurrentDate, removeHyphens } from "@services/Utilities";
import { error_message } from "@constants/Errors";
import messages from "@constants/Messages";
import { POLLING_MESSAGES } from "@constants/Enums";
import { KAKELY_PATHS } from "@constants/NavigationPaths";
import { USER } from "@types/user";
import { METHOD_TYPE } from "@constants/Enums";

export const UseKakelyCsvUpload = () => {
  const navigate: NavigateFunction = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const csvNotFoundErrorMessage = "Couldn't find User with 'id'";
  const deleteCsvNotFoundMessage = "csv:delete_id_not_found";
  const createCsvNotFoundMessage = "csv:create_id_not_found";
  const updatedCsvNotFoundMessage = "csv:update_id_not_found";

  const currentDate = GetCurrentDate();

  const noCsvImportError = () => toast.error(error_message.csv.no_data);
  const uploadCsvDataToast = (message) => toast.success(message);
  const failCsvDataToast = (error) => toast.error(`${error}`);
  const [csvData, setCsvData] = useState<[]>([]);
  const [failedUsers, setFailedUsers] = useState<[]>();

  const [isProcessing, setIsProcessing] = useState(false);
  const [procesedCount, setProcesedCount] = useState(0);

  const handleCsvUpload = async (userType: number) => {
    if (csvData.length === 0) {
      noCsvImportError();
      return;
    }

    const userDataArray = csvData.map((userInfo: USER) => {
      const user = {
        id: userInfo.id,
        last_name: userInfo.last_name,
        first_name: userInfo.first_name,
        last_kana_name: userInfo.last_kana_name,
        first_kana_name: userInfo.first_kana_name,
        last_furigana_name: userInfo.last_furigana_name,
        first_furigana_name: userInfo.first_furigana_name,
        email: userInfo.email,
        user_type: userType,
        is_display: userInfo.is_display,
        position_name: userInfo.position_name,
        company_name: userInfo.company_name,
        company_tel: removeHyphens(userInfo.company_tel),
        company_fax: removeHyphens(userInfo.company_fax),
        personal_tel: removeHyphens(userInfo.personal_tel),
        company_address: userInfo.company_address,
        company_postal_code: userInfo.company_postal_code,
        company_home_page: userInfo.company_home_page,
        delete: userInfo.delete,
        row: csvData.indexOf(userInfo) + 2,
      };

      return { userInfo: user };
    });

    const postUserInformation = async (userDataArray) => {
      let response;
      try {
        response = await postContactArray(userDataArray, setLoading, setError);
        if (
          response.responseMessage.status >= 300 ||
          response.responseMessage.status == null
        ) {
          const errorMessage = TranslateErrorMessage(response.responseMessage);
          setFailedUsers(response.failed_users);
          console.error(errorMessage);
          setError(errorMessage);
          failCsvDataToast(response.failed_users.error);
          return;
        } else if (response.responseMessage.status == 202) {
          response = await pollForStatus(
            response.request_id,
            userDataArray.length,
            1000,
            5000,
          );
        } else {
          uploadCsvDataToast(messages.user.single_user_uploaded);
        }
        setFailedUsers(response.failed_users);
      } catch (error) {
        if (userDataArray.length == 1 && response.failed_users) {
          failCsvDataToast(response.failed_users.error);
          return;
        }

        if (response && response.failed_users) {
          setFailedUsers(response.failed_users);
        }

        const errorMessage = TranslateErrorMessage(response.responseMessage);
        console.error(errorMessage);
        setError(errorMessage);

        const toastFunction =
          response.created_users.length <= 0
            ? uploadCsvDataToast
            : failCsvDataToast;

        const createdCount =
          response.created_users.length ||
          (response.created_users.length ? 1 : 0);
        const failedCount =
          response.failed_users.length ||
          (response.created_users.length ? 0 : 1);

        toastFunction(messages.user.uploaded(createdCount, failedCount));
      }
    };
    await postUserInformation(userDataArray);
  };

  const onCancel = () => {
    navigate(KAKELY_PATHS.main);
  };

  const pollForStatus = async (
    requestId,
    totalUsers,
    poll_interval,
    timeout,
  ) => {
    const TIMEOUT = timeout * totalUsers;
    const startTime = Date.now();

    setIsProcessing(true);

    const poll = async () => {
      try {
        const statusResponse = await getUploadStatus(requestId, totalUsers);

        const { message, processed_count, total_users } = statusResponse.data;

        setProcesedCount(processed_count);

        if (
          message !== POLLING_MESSAGES.PROCESSING &&
          processed_count === total_users
        ) {
          setIsProcessing(false);
          processResult(statusResponse.data);
          return statusResponse.data;
        }

        if (Date.now() - startTime > TIMEOUT) {
          setIsProcessing(false);
          setError(error_message.csv.timeout);
          failCsvDataToast(error_message.csv.timeout);
          failCsvDataToast(error_message.csv.timeout_users_created);
          return;
        }

        setTimeout(poll, poll_interval);
      } catch (error) {
        if (error.status === 401) {
          setTimeout(poll, 5000);
          return;
        }

        setError(error_message.csv.polling_error);
        failCsvDataToast(error_message.csv.polling_error);
      }
    };

    poll();
  };

  const processResult = (finalResult) => {
    const numberOfUsers = csvData.length;

    if (finalResult.failed_users.length > 0) {
      const message =
        numberOfUsers == 1
          ? error_message.user.single_user_upload_failed
          : messages.user.uploaded(
              finalResult.created_users.length,
              finalResult.failed_users.length,
              false,
            );

      failCsvDataToast(message);
    } else {
      const message =
        numberOfUsers === 1
          ? messages.user.single_user_uploaded
          : messages.user.uploaded(
              finalResult.created_users.length,
              finalResult.failed_users.length,
              false,
            );
      uploadCsvDataToast(message);
    }

    finalResult.failed_users?.forEach((user) => {
      if (
        user.error.includes(csvNotFoundErrorMessage) &&
        user.method === METHOD_TYPE.DELETE
      ) {
        if (user.error.includes("'id'=0")) {
          const message = TranslateErrorMessage(createCsvNotFoundMessage);
          user.error = message;
          return;
        } else {
          const message = TranslateErrorMessage(deleteCsvNotFoundMessage);
          user.error = message;
          return;
        }
      } else if (
        user.error.includes(csvNotFoundErrorMessage) &&
        user.method === METHOD_TYPE.UPDATE
      ) {
        const message = TranslateErrorMessage(updatedCsvNotFoundMessage);
        user.error = message;
        return;
      }
      const translatedMessage = TranslateErrorMessage(user.error);
      user.error = translatedMessage;
    });

    setFailedUsers(finalResult.failed_users);
  };

  return {
    loading,
    isProcessing,
    procesedCount,
    csvData,
    failedUsers,
    currentDate,
    handleCsvUpload,
    onCancel,
    setCsvData,
  };
};
